/**
 * Cancellation hooks -- TanStack Query v5 for cancellation preview and mutation.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cancellationService } from '../services/cancellation.service'
import { bookingKeys } from './use-booking'
import type { CancellationReason } from '../types/cancellation.types'

// ─── Query Keys ─────────────────────────────────────────────────────────────

const cancellationKeys = {
  all: ['cancellation'] as const,
  preview: (bookingId: string) => [...cancellationKeys.all, 'preview', bookingId] as const,
}

// ─── Cancellation Preview ───────────────────────────────────────────────────

export function useCancellationPreview(bookingId: string | undefined) {
  return useQuery({
    queryKey: cancellationKeys.preview(bookingId ?? ''),
    queryFn: () => cancellationService.previewCancellation(bookingId!),
    enabled: !!bookingId,
    retry: false,
    staleTime: 30 * 1000, // 30s -- refund amount changes as time passes
  })
}

// ─── Cancel Booking ─────────────────────────────────────────────────────────

export function useCancelBooking() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: ({
      userId,
      bookingId,
      reason,
    }: {
      userId: string
      bookingId: string
      reason: CancellationReason | string
    }) => cancellationService.cancelBooking(userId, bookingId, reason),

    onSuccess: (_data, { bookingId }) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.all })
      queryClient.invalidateQueries({ queryKey: cancellationKeys.preview(bookingId) })
    },
  })

  return {
    cancelBooking: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}
